/**
 * ListDataRow — the single source of truth for a data row in App-mode lists.
 *
 * Extracted from `RuntimeListView`'s virtualized body so the flat list and the
 * lazy group tree (`RuntimeGroupTreeView`) render rows IDENTICALLY: same cell
 * formatting, deleted-row styling, and a row overflow menu.
 * Lives in its own module to keep both consumers free of a circular
 * import (RuntimeListView ⇄ RuntimeGroupTreeView).
 */
import React from 'react'
import Box from '@mui/material/Box'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Checkbox from '@mui/material/Checkbox'
import PrintIcon from '@mui/icons-material/Print'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DeleteIcon from '@mui/icons-material/Delete'
import RestoreIcon from '@mui/icons-material/Restore'
import { ListRowOverflow, LIST_ROW_OVERFLOW_COL_WIDTH, type ListRowOverflowItem } from '@/shared/listTable'
import { renderListCell, monoSx } from '@/shared/listCell'
import type { ListColumnSpec } from './listSchema'
import { widthSx } from './listSchema'
import { isNumericListColumn, type ListCellAttrHint } from '@/shared/formatListCellValue'
import { listCellHairlineSx, listDensityCellSx, listDensityControlSx, listNumericBodySx } from '@/shared/listTable/listTableChrome'
import { useDensity } from '@/shared/density/DensityProvider'
import { InlineCellEditor } from './InlineCellEditor'
import type { AttributeDef } from '../designer/services/configObjectApi'
import { HierarchyKindIcon } from '@/shared/HierarchyKindIcon'
import { isPredefinedRecord } from './appHiddenAttributes'

/** Delay before a single click on an editable cell opens the row — gives a
 * follow-up click time to arrive and be recognized as a double-click (edit). */
const CELL_OPEN_DELAY_MS = 180

/** Minimal print surface ListDataRow needs — a subset of `usePrintCommands`. */
export interface ListRowPrint {
  hasPrintLayouts: boolean
  printLoading: boolean
  openLayoutChoice: (anchorEl: HTMLElement, record: Record<string, unknown>, mode: 'print' | 'pdf') => void
}

export interface ListDataRowProps {
  row: Record<string, any>
  columns: ListColumnSpec[]
  hintFor: (col: ListColumnSpec) => ListCellAttrHint | undefined
  rowHeight: number
  print: ListRowPrint
  onEdit: (row: Record<string, any>) => void
  onDelete: (recordId: string, wasMarked?: boolean) => void
  onUndelete: (recordId: string) => void
  /** True when this row is the keyboard-nav active row (Operator Lists v2 — flat list only). */
  active?: boolean
  /**
   * Hierarchy tree indent (Predefined hierarchy UX — `RuntimeHierarchyTreeView`
   * only). Applied as extra left padding on the first cell, matching the
   * group row's own `1 + depth * 2` spacing-unit formula so leaf rows line
   * up under their parent group's label. Undefined for the flat list —
   * zero behavior change.
   */
  depth?: number
  /**
   * Leading content prepended inside the presentation (kind-icon) cell, before
   * its normal value (hierarchy tree — `HierarchyKindIcon`). Undefined for the
   * flat list.
   */
  leading?: React.ReactNode
  /**
   * Column that hosts `leading` + depth indent + predefined lock.
   * Defaults to the first column when omitted. App lists always pass columns[0]
   * so icons stay leftmost after reorder.
   */
  kindIconColumn?: string | null
  /**
   * Selection column (Operator Lists v2.1 — flat list only). `RuntimeGroupTreeView`
   * never passes these, so the checkbox cell renders only when `selectable` is
   * truthy — the group tree keeps its pre-selection layout unchanged.
   */
  selectable?: boolean
  selected?: boolean
  onToggleSelect?: (e: React.MouseEvent, index: number) => void
  rowIndex?: number
  /**
   * Inline cell editing (Operator Lists v2.1, Task 12 — flat list only;
   * `RuntimeGroupTreeView` never passes these, so group rows stay read-only).
   * A cell is editable when `editable && !!attrFor?.(col)`.
   */
  editable?: boolean
  editState?: { rowId: string; field: string } | null
  editDraft?: unknown
  attrFor?: (col: ListColumnSpec) => AttributeDef | undefined
  connectionId?: string
  onBeginEdit?: (rowId: string, field: string, current: unknown) => void
  onEditChange?: (v: unknown) => void
  onEditCommit?: () => void
  onEditCancel?: () => void
  /**
   * When true, Delete stays available for predefined rows (Designer Predefined
   * authoring via HierarchyTreeView). App leaves this unset/false.
   */
  allowDeletePredefined?: boolean
  /**
   * Effective delete permission on this object (Task 12, ERP Users & Access
   * Rights epic). Defaults to true — omitted, Delete renders exactly as
   * before this epic (subject only to the predefined-record guard above).
   */
  canDelete?: boolean
}

export const ListDataRow: React.FC<ListDataRowProps> = ({
  row,
  columns,
  hintFor,
  rowHeight,
  print,
  onEdit,
  onDelete,
  onUndelete,
  active,
  depth,
  leading,
  kindIconColumn,
  selectable,
  selected,
  onToggleSelect,
  rowIndex,
  editable,
  editState,
  editDraft,
  attrFor,
  connectionId,
  onBeginEdit,
  onEditChange,
  onEditCommit,
  onEditCancel,
  allowDeletePredefined = false,
  canDelete = true,
}) => {
  const { density, tokens } = useDensity()
  const cellDensitySx = listDensityCellSx(tokens)
  const controlDensitySx = listDensityControlSx(density)
  const isDeleted = row.is_deleted === true || row.is_deleted === 1
  // Predefined (Designer-seeded) rows are Designer-only to delete — the App
  // never surfaces a Delete action for them (server 403 is the backstop).
  // Designer Predefined authoring passes allowDeletePredefined to keep Delete.
  const predefined = isPredefinedRecord(row)
  const hideDelete = (predefined && !allowDeletePredefined) || !canDelete
  const kebabRef = React.useRef<HTMLButtonElement>(null)

  // Click-delay guard: a single click on an editable cell schedules the row-open
  // after CELL_OPEN_DELAY_MS; a double-click within that window clears the timer
  // and enters edit mode instead. Non-editable cells open instantly, unchanged.
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  // Clear a pending open-delay timer on unmount: in the virtualized list a row
  // can unmount within the 180ms window (fast scroll), and without this a
  // stale `onEdit(row)` would still fire and open the CRUD dialog for a
  // scrolled-away row.
  React.useEffect(() => () => { if (openTimer.current) clearTimeout(openTimer.current) }, [])
  const editableCol = (col: ListColumnSpec) => !!editable && !!attrFor?.(col)
  const cellClick = (col: ListColumnSpec) => {
    if (!editableCol(col)) { onEdit(row); return }
    if (openTimer.current) clearTimeout(openTimer.current)
    openTimer.current = setTimeout(() => { onEdit(row); openTimer.current = null }, CELL_OPEN_DELAY_MS)
  }
  const cellDoubleClick = (col: ListColumnSpec, e: React.MouseEvent) => {
    if (!editableCol(col)) return
    e.stopPropagation()
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null }
    onBeginEdit?.(String(row.id), col.name, (row as Record<string, unknown>)[col.name])
  }

  return (
    <TableRow
      hover
      className="list-data-row"
      data-density={density}
      sx={{
        cursor: 'pointer',
        height: rowHeight,
        opacity: isDeleted ? 0.5 : 1,
        textDecoration: isDeleted ? 'line-through' : 'none',
        // Keyboard-nav active row: token accent left-border (Task 7).
        ...(active
          ? { boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}` }
          : {}),
      }}
    >
      {selectable && (
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ ...cellDensitySx, ...listCellHairlineSx }}>
          <Checkbox
            size="small"
            sx={controlDensitySx}
            checked={!!selected}
            onClick={(e) => onToggleSelect?.(e, rowIndex ?? -1)}
            slotProps={{ input: { 'aria-label': 'Select row' } }}
          />
        </TableCell>
      )}
      {columns.map((col, idx) => {
        const isEditingThis = editState?.rowId === String(row.id) && editState?.field === col.name
        const attr = attrFor?.(col)
        const isIconCol = kindIconColumn
          ? col.name === kindIconColumn
          : idx === 0
        const cellValue = isEditingThis && attr
          ? (
            <InlineCellEditor
              attr={attr}
              value={editDraft}
              connectionId={connectionId ?? ''}
              onChange={(v) => onEditChange?.(v)}
              onCommit={() => onEditCommit?.()}
              onCancel={() => onEditCancel?.()}
            />
          )
          : renderListCell(col, row, hintFor(col))
        return (
          <TableCell
            key={col.name}
            sx={{
              ...widthSx(col.width),
              ...(col.mono ? monoSx : {}),
              cursor: 'pointer',
              ...(isIconCol && depth != null ? { pl: 1 + depth * 2 } : {}),
              ...cellDensitySx,
              ...listCellHairlineSx,
              ...listNumericBodySx(isNumericListColumn(hintFor(col))),
            }}
            onClick={() => { if (!isEditingThis) cellClick(col) }}
            onDoubleClick={(e) => cellDoubleClick(col, e)}
          >
            {isIconCol && (leading || predefined) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {leading ?? (predefined ? <HierarchyKindIcon kind="element" locked /> : null)}
                {cellValue}
              </Box>
            ) : cellValue}
          </TableCell>
        )
      })}
      <TableCell
        className="list-row-actions"
        align="right"
        onClick={(e) => e.stopPropagation()}
        sx={{ width: LIST_ROW_OVERFLOW_COL_WIDTH, ...cellDensitySx, ...listCellHairlineSx }}
      >
        <ListRowOverflow
          buttonRef={kebabRef}
          items={((): ListRowOverflowItem[] => {
            const items: ListRowOverflowItem[] = []
            if (print.hasPrintLayouts && row.id) {
              const fire = (kind: 'print' | 'pdf') => {
                const el = kebabRef.current
                if (!el) return
                print.openLayoutChoice(el, row, kind)
              }
              items.push({
                id: 'print',
                label: 'Print',
                icon: <PrintIcon fontSize="small" />,
                disabled: print.printLoading,
                onClick: () => fire('print'),
              })
              items.push({
                id: 'pdf',
                label: 'PDF',
                icon: <PictureAsPdfIcon fontSize="small" />,
                disabled: print.printLoading,
                onClick: () => fire('pdf'),
              })
            }
            if (isDeleted) {
              items.push({
                id: 'restore',
                label: 'Restore',
                icon: <RestoreIcon fontSize="small" />,
                color: 'success',
                onClick: () => onUndelete(String(row.id)),
              })
            } else if (!hideDelete) {
              items.push({
                id: 'delete',
                label: 'Delete',
                icon: <DeleteIcon fontSize="small" />,
                color: 'error',
                onClick: () => onDelete(String(row.id), isDeleted),
              })
            }
            return items
          })()}
        />
      </TableCell>
    </TableRow>
  )
}
